import {
  CLIENT_EVENTS,
  SERVER_EVENTS,
  type MatchFoundPayload,
  type MatchResult,
  type MinigameId,
  type OpponentProgressPayload,
} from '@minigame-race/contracts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connectSocket, serverUrl, type GameSocket } from './socket.js';
import { useSmoothClock } from './useSmoothClock.js';
import { play as playSound } from './sound.js';
import type { AuthUser } from './firebase.js';

export type Screen =
  | 'home'
  | 'rules'
  | 'stats'
  | 'lab'
  | 'searching'
  | 'room'
  | 'join-room'
  | 'countdown'
  | 'race'
  | 'result';

export interface MatchHistoryEntry {
  matchId: string;
  playedAt: string;
  opponentName: string;
  ranked: boolean;
  outcome: 'win' | 'loss' | 'draw';
  yourPoints: number;
  opponentPoints: number;
  eloDelta: number | null;
  recipe: string[];
}
export interface EloPoint { at: string; elo: number; }
export interface PerGameStat { minigameKey: string; played: number; solved: number; firstToSolve: number; avgPoints: number; }
export interface PlayerStats { history: MatchHistoryEntry[]; eloTrend: EloPoint[]; perGame: PerGameStat[]; }

/** A reaction currently floating on screen. `id` is unique so keys are stable. */
export interface LiveReaction { id: number; reaction: string; fromYou: boolean; }

/** Rematch state for the just-finished match (drives the result-screen button). */
export interface RematchState { matchId: string; youOffered: boolean; opponentOffered: boolean; opponentAvailable: boolean; }

export interface Profile {
  id: string;
  displayName: string;
  isAnonymous: boolean;
  /** True when a signed-up user hasn't picked a nickname yet — show the prompt. */
  needsNickname: boolean;
  elo: number;
  rank: string;
  wins: number;
  losses: number;
  winRate: number;
}

/** The between-segments reveal: did each of us land it? */
export interface SegmentReveal {
  segmentIndex: number;
  minigameId: MinigameId;
  youSolved: boolean;
  opponentSolved: boolean;
  /** True if you got there first. Null if nobody solved it. */
  youWereFirst: boolean | null;
  raceComplete: boolean;
}

export interface RaceState {
  matchId: string;
  recipe: readonly MinigameId[];
  ranked: boolean;
  segmentIndex: number;
  minigameId: MinigameId;
  /** This player's authoritative view, straight from the engine. */
  view: unknown;
  /** True once one player has solved the segment and grace is ticking. */
  graceActive: boolean;
  /** True once *you* have solved it — you're watching, not playing. */
  youSolved: boolean;
  /** Set between segments, cleared when the next one opens. */
  reveal: SegmentReveal | null;
  finished: boolean;
  opponent: { progressPct: number; segmentIndex: number; finished: boolean; view: unknown };
}

export interface GameState {
  screen: Screen;
  profile: Profile | null;
  match: MatchFoundPayload | null;
  countdownStartsAt: number | null;
  /** Local performance.now() at which the race clock started. */
  raceStartedAt: number | null;
  race: RaceState | null;
  result: MatchResult | null;
  roomCode: string | null;
  playersInQueue: number;
  /** Set while our socket is down and the reconnect window is ticking. */
  disconnected: boolean;
  opponentDisconnected: boolean;
  error: string | null;
  /** Bumped on every rejected event so the UI can flash a miss. */
  rejectNonce: number;
  lastReject: string | null;
  reactions: LiveReaction[];
  rematch: RematchState | null;
}

const initialState: GameState = {
  screen: 'home',
  profile: null,
  match: null,
  countdownStartsAt: null,
  raceStartedAt: null,
  race: null,
  result: null,
  roomCode: null,
  playersInQueue: 0,
  disconnected: false,
  opponentDisconnected: false,
  error: null,
  rejectNonce: 0,
  lastReject: null,
  reactions: [],
  rematch: null,
};

export function useGame(user: AuthUser) {
  const [state, setState] = useState<GameState>(initialState);
  const socketRef = useRef<GameSocket | null>(null);
  /** Mirror of the live matchId, readable from callbacks without stale closures. */
  const matchIdRef = useRef<string | null>(null);
  /** Monotonic id for floating reactions, so React keys stay stable. */
  const reactionSeq = useRef(0);
  /** Counts the segment/grace clock down. Both players share it. */
  const clock = useSmoothClock();

  matchIdRef.current = state.race?.matchId ?? state.match?.matchId ?? null;

  // Fetch the profile the Home screen renders. Lazily provisions the row.
  useEffect(() => {
    let cancelled = false;
    void fetch(`${serverUrl}/api/me`, { headers: { Authorization: `Bearer ${user.token}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((profile: Profile) => {
        if (!cancelled) setState((s) => ({ ...s, profile }));
      })
      .catch(() => {
        if (!cancelled) setState((s) => ({ ...s, error: 'Could not load profile' }));
      });
    return () => {
      cancelled = true;
    };
  }, [user.token]);

  useEffect(() => {
    const socket = connectSocket(user.token);
    socketRef.current = socket;

    socket.on('connect', () => setState((s) => ({ ...s, disconnected: false, error: null })));
    socket.on('disconnect', () => setState((s) => ({ ...s, disconnected: true })));

    // A rejected handshake lands here. Without surfacing it the socket simply
    // never connects, every emit() buffers silently, and the UI looks frozen —
    // buttons "do nothing". Show the server's reason instead.
    socket.on('connect_error', (err: Error) => {
      setState((s) => ({
        ...s,
        disconnected: true,
        error: err.message === 'unauthorized' ? 'Sign-in rejected by server' : err.message,
      }));
    });

    socket.on(SERVER_EVENTS.queueStatus, (p) => {
      setState((s) => ({
        ...s,
        playersInQueue: p.playersInQueue,
        screen: p.inQueue ? 'searching' : s.screen === 'searching' ? 'home' : s.screen,
      }));
    });

    socket.on(SERVER_EVENTS.roomCreated, (p) => {
      setState((s) => ({ ...s, roomCode: p.code, screen: 'room' }));
    });

    socket.on(SERVER_EVENTS.matchFound, (p) => {
      setState((s) => ({
        ...s,
        match: p,
        result: null,
        rematch: null,
        roomCode: null,
        raceStartedAt: null,
        race: {
          matchId: p.matchId,
          recipe: p.recipe,
          ranked: p.ranked,
          segmentIndex: 0,
          minigameId: p.recipe[0] ?? 'tile-tap',
          view: null,
          graceActive: false,
          youSolved: false,
          reveal: null,
          finished: false,
          opponent: { progressPct: 0, segmentIndex: 0, finished: false, view: null },
        },
      }));
    });

    socket.on(SERVER_EVENTS.matchCountdown, (p) => {
      // Correct for clock skew between this browser and the server.
      const skew = p.serverNow - Date.now();
      setState((s) => ({ ...s, countdownStartsAt: p.startsAt - skew, screen: 'countdown' }));
    });

    socket.on(SERVER_EVENTS.matchClock, (p) => {
      clock.sync(p.segmentRemainingMs);
      setState((s) =>
        s.race && (s.race.youSolved !== p.youSolved || s.race.graceActive !== p.graceActive)
          ? { ...s, race: { ...s.race, youSolved: p.youSolved, graceActive: p.graceActive } }
          : s,
      );
      // A clock tick means the race is live. This is what moves us off the
      // countdown, and also what lands a reconnecting client (whose screen is
      // still 'home' after a reload) straight into the race.
      setState((s) => {
        if (s.screen === 'race' && s.raceStartedAt !== null) return s;
        return {
          ...s,
          screen: s.race ? 'race' : s.screen,
          raceStartedAt: s.raceStartedAt ?? performance.now(),
        };
      });
    });

    socket.on(SERVER_EVENTS.minigameState, (p) => {
      setState((s) =>
        s.race
          ? {
              ...s,
              screen: s.screen === 'countdown' ? 'race' : s.screen,
              race: {
                ...s.race,
                segmentIndex: p.segmentIndex,
                minigameId: p.minigameId,
                view: p.view,
                reveal: null,
              },
            }
          : s,
      );
    });

    socket.on(SERVER_EVENTS.segmentAdvance, (p) => {
      if (p.youSolved) playSound('solve');
      // The segment closed. Show the reveal; the server opens the next segment
      // after `interstitialMs` and its minigame:state clears this.
      setState((s) => {
        if (!s.race) return s;
        const closedId = s.race.recipe[p.completedSegmentIndex] ?? s.race.minigameId;
        const youWereFirst =
          p.firstFinisherId === null ? null : p.firstFinisherId === s.match?.you.userId;
        return {
          ...s,
          race: {
            ...s.race,
            finished: p.raceComplete,
            view: null,
            graceActive: false,
            youSolved: false,
            reveal: {
              segmentIndex: p.completedSegmentIndex,
              minigameId: closedId,
              youSolved: p.youSolved,
              opponentSolved: p.opponentSolved,
              youWereFirst,
              raceComplete: p.raceComplete,
            },
          },
        };
      });
    });

    socket.on(SERVER_EVENTS.opponentProgress, (p: OpponentProgressPayload) => {
      setState((s) =>
        s.race
          ? {
              ...s,
              race: {
                ...s.race,
                opponent: {
                  progressPct: p.progressPct,
                  segmentIndex: p.segmentIndex,
                  finished: p.finished,
                  view: p.view,
                },
              },
            }
          : s,
      );
    });

    socket.on(SERVER_EVENTS.segmentGrace, (p) => {
      playSound(p.youFinished ? 'solve' : 'oppFinish');
      // Grace only ever pulls the deadline in, so re-anchor immediately rather
      // than waiting up to a full tick showing time that no longer exists.
      clock.sync(p.remainingMs);
      setState((s) =>
        s.race
          ? { ...s, race: { ...s.race, graceActive: true, youSolved: p.youFinished } }
          : s,
      );
    });

    socket.on(SERVER_EVENTS.matchResult, (p) => {
      clock.reset();
      setState((s) => ({ ...s, result: p.result, screen: 'result', race: null, match: null, raceStartedAt: null, rematch: null, reactions: [] }));
    });

    socket.on(SERVER_EVENTS.rematchState, (p) => {
      setState((s) => ({ ...s, rematch: p }));
    });

    socket.on(SERVER_EVENTS.eventRejected, (p) => {
      playSound('wrong');
      setState((s) => ({ ...s, rejectNonce: s.rejectNonce + 1, lastReject: p.reason }));
    });

    socket.on(SERVER_EVENTS.reaction, (p) => {
      const id = reactionSeq.current++;
      playSound(p.fromYou ? 'emoteYou' : 'emoteRival'); // a little pop as it lands
      setState((s) => ({ ...s, reactions: [...s.reactions, { id, reaction: p.reaction, fromYou: p.fromYou }] }));
      // Auto-remove after the float animation so the list never grows unbounded.
      window.setTimeout(() => {
        setState((s) => ({ ...s, reactions: s.reactions.filter((r) => r.id !== id) }));
      }, 1800);
    });

    socket.on(SERVER_EVENTS.opponentDisconnected, () => {
      setState((s) => ({ ...s, opponentDisconnected: true }));
    });
    socket.on(SERVER_EVENTS.opponentReconnected, () => {
      setState((s) => ({ ...s, opponentDisconnected: false }));
    });

    socket.on(SERVER_EVENTS.error, (p) => {
      setState((s) => {
        // A failed room join (bad/expired code, host gone) must not strand the
        // joiner on the "joining…" screen — send them home with the message.
        const roomJoinFailed =
          s.screen === 'join-room' &&
          (p.code === 'room_not_found' || p.code === 'cannot_join_own_room' || p.code === 'match_failed');
        return { ...s, error: p.message, ...(roomJoinFailed ? { screen: 'home' as const, roomCode: null } : {}) };
      });
    });

    return () => {
      socket.removeAllListeners();
      socket.close();
      socketRef.current = null;
    };
    // clock's identity is stable (refs + setState only).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.token]);

  const actions = useMemo(
    () => ({
      joinQueue: () => socketRef.current?.emit(CLIENT_EVENTS.queueJoin),
      leaveQueue: () => {
        socketRef.current?.emit(CLIENT_EVENTS.queueLeave);
        setState((s) => ({ ...s, screen: 'home' }));
      },
      createRoom: () => socketRef.current?.emit(CLIENT_EVENTS.roomCreate),
      joinRoom: (code: string) => {
        // Show a "joining…" screen so the URL (/room/CODE) and the view agree
        // while we wait for the host. matchFound moves us on; a room error (see
        // the error handler) bounces back home.
        setState((s) => ({ ...s, screen: 'join-room', roomCode: code }));
        socketRef.current?.emit(CLIENT_EVENTS.roomJoin, { code });
      },
      cancelRoom: () => {
        socketRef.current?.emit(CLIENT_EVENTS.roomCancel);
        setState((s) => ({ ...s, roomCode: null, screen: 'home' }));
      },
      leaveMatch: (matchId: string) =>
        socketRef.current?.emit(CLIENT_EVENTS.matchLeave, { matchId }),
      /** Emits an opaque minigame event. The payload is the game's own shape. */
      sendEvent: (matchId: string, segmentIndex: number, event: unknown) =>
        socketRef.current?.emit(CLIENT_EVENTS.minigameEvent, { matchId, segmentIndex, event }),
      /** Fire an emote. The server relays it to both players. */
      sendReaction: (matchId: string, reaction: string) =>
        socketRef.current?.emit(CLIENT_EVENTS.reaction, { matchId, reaction }),
      /** Offer a rematch of the just-finished match to the same opponent. */
      offerRematch: (matchId: string) =>
        socketRef.current?.emit(CLIENT_EVENTS.rematchOffer, { matchId }),
      cancelRematch: (matchId: string) =>
        socketRef.current?.emit(CLIENT_EVENTS.rematchCancel, { matchId }),
      backToLobby: () => setState((s) => ({ ...s, screen: 'home', result: null })),
      /**
       * Return to home from a NAVIGABLE screen (home/play/stats/rules/lab/room).
       * Used by the router on back-navigation. Deliberately does NOT touch an
       * in-progress match's state — those screens are ephemeral and are left to
       * their own leave/abandon flows.
       */
      goHome: () =>
        setState((s) => {
          const navigable =
            s.screen === 'searching' || s.screen === 'room' || s.screen === 'join-room' || s.screen === 'stats' || s.screen === 'rules' || s.screen === 'lab';
          if (s.screen === 'searching') socketRef.current?.emit(CLIENT_EVENTS.queueLeave);
          if (s.screen === 'room') socketRef.current?.emit(CLIENT_EVENTS.roomCancel);
          return navigable ? { ...s, screen: 'home', roomCode: null } : s;
        }),
      showRules: () => setState((s) => ({ ...s, screen: 'rules' })),
      hideRules: () => setState((s) => (s.screen === 'rules' ? { ...s, screen: 'home' } : s)),
      showStats: () => setState((s) => ({ ...s, screen: 'stats' })),
      hideStats: () => setState((s) => (s.screen === 'stats' ? { ...s, screen: 'home' } : s)),
      showLab: () => setState((s) => ({ ...s, screen: 'lab' })),
      hideLab: () => setState((s) => (s.screen === 'lab' ? { ...s, screen: 'home' } : s)),
      /**
       * Escape hatch from the recovery screen. If the server still thinks we're
       * in a match, forfeit it — otherwise the next FIND MATCH is refused with
       * `already_in_match` and the player is stuck for good.
       *
       * Reads the id from a ref, not from inside setState: an emit is a side
       * effect, and StrictMode invokes reducers twice.
       */
      abandonMatch: () => {
        const id = matchIdRef.current;
        if (id) socketRef.current?.emit(CLIENT_EVENTS.matchLeave, { matchId: id });
        setState((s) => ({
          ...s,
          screen: 'home',
          race: null,
          match: null,
          result: null,
          raceStartedAt: null,
        }));
      },
      dismissError: () => setState((s) => ({ ...s, error: null })),
    }),
    [],
  );

  const refreshProfile = useCallback(() => {
    void fetch(`${serverUrl}/api/me`, { headers: { Authorization: `Bearer ${user.token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((p: Profile | null) => {
        if (p) setState((s) => ({ ...s, profile: p }));
      })
      .catch(() => undefined);
  }, [user.token]);

  /**
   * Save the nickname a new user just chose. Updates the profile in place on
   * success (which clears `needsNickname` and hides the prompt); returns an
   * error message on failure so the overlay can show it.
   */
  const saveNickname = useCallback(
    async (nickname: string): Promise<string | null> => {
      try {
        const r = await fetch(`${serverUrl}/api/me/nickname`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${user.token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ nickname }),
        });
        if (r.ok) {
          const p = (await r.json()) as Profile;
          setState((s) => ({ ...s, profile: p }));
          // The socket captured the OLD displayName at handshake (it connected on
          // mount, before the nickname existed). Reconnect so the handshake
          // re-reads the fresh name from the DB — otherwise a rival would see the
          // pre-nickname default on the board.
          socketRef.current?.disconnect().connect();
          return null;
        }
        const body = (await r.json().catch(() => null)) as { message?: string } | null;
        return body?.message ?? 'Could not save nickname';
      } catch {
        return 'Could not reach the server';
      }
    },
    [user.token],
  );

  const fetchStats = useCallback(async (): Promise<PlayerStats | null> => {
    try {
      const r = await fetch(`${serverUrl}/api/stats`, { headers: { Authorization: `Bearer ${user.token}` } });
      return r.ok ? ((await r.json()) as PlayerStats) : null;
    } catch {
      return null;
    }
  }, [user.token]);

  /**
   * Time on the clock so far. Measured from when the race started, NOT as
   * `budget - remaining` — the grace window clamps the deadline, which would
   * make that expression jump backwards by minutes.
   */
  const [elapsedMs, setElapsedMs] = useState(0);
  const startedAt = state.raceStartedAt;
  useEffect(() => {
    if (startedAt === null) {
      setElapsedMs(0);
      return;
    }
    let raf = 0;
    const tick = (): void => {
      setElapsedMs(performance.now() - startedAt);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [startedAt]);

  return { state, actions, remainingMs: clock.remainingMs, elapsedMs, refreshProfile, fetchStats, saveNickname };
}
